import { format, parseISO } from "date-fns";
import { pluralize } from "@common/helpers/string";
import type { CoADocumentMigrationSource } from "@common/schemas/accounting-integration.schema";
import type {
    MigrationPeriod,
    MigrationStatusText,
} from "./migrationFileMatching.types";

const MIGRATION_UNRECOGNIZED_MESSAGE: MigrationStatusText = {
    short: "Unreadable file",
    tooltip: "Not a recognized export format",
};

const XERO_UNRECOGNIZED_MESSAGE: MigrationStatusText = {
    short: "Unrecognized",
    tooltip: "Not a Xero chart of accounts or general ledger",
};

const QBO_UNRECOGNIZED_MESSAGE: MigrationStatusText = {
    short: "Unrecognized",
    tooltip: "Not a QuickBooks Online chart of accounts or general ledger",
};

export function unrecognizedMessageForSource(
    source: CoADocumentMigrationSource,
): MigrationStatusText {
    if (source === "xero") {
        return XERO_UNRECOGNIZED_MESSAGE;
    }
    if (source === "qbo") {
        return QBO_UNRECOGNIZED_MESSAGE;
    }
    return MIGRATION_UNRECOGNIZED_MESSAGE;
}

function isFullCalendarYear(startDate: Date, endDate: Date): boolean {
    return (
        startDate.getFullYear() === endDate.getFullYear() &&
        startDate.getMonth() === 0 &&
        endDate.getMonth() === 11
    );
}

// Periods summarize at month granularity: a month with any entry counts as
// covered, so day-level boundaries are noise in the period column.
export function formatPeriod(period?: MigrationPeriod | null): string {
    if (!period) {
        return "";
    }
    if (!period.start) {
        if (!period.end) {
            return "";
        }
        return `through ${format(parseISO(period.end), "MMM yyyy")}`;
    }
    const startDate = parseISO(period.start);
    const endDate = period.end ? parseISO(period.end) : startDate;
    if (isFullCalendarYear(startDate, endDate)) {
        return String(startDate.getFullYear());
    }
    if (
        startDate.getFullYear() === endDate.getFullYear() &&
        startDate.getMonth() === endDate.getMonth()
    ) {
        return format(startDate, "MMM yyyy");
    }
    if (startDate.getFullYear() === endDate.getFullYear()) {
        return `${format(startDate, "MMM")} – ${format(endDate, "MMM yyyy")}`;
    }
    return `${format(startDate, "MMM yyyy")} – ${format(endDate, "MMM yyyy")}`;
}

export function formatCoaSummary(count: number): string {
    return `${count} ${pluralize("account", count)} found`;
}

export function formatGlSummary(count: number): string {
    return `${count} ${pluralize("entry", count)} found`;
}
